'use client';

import { ehrRePairRequiredAtomFamily } from '@/atoms/integrations/integrationAtoms';
import {
  Banner,
  BannerAction,
  BannerContent,
  BannerDescription,
  BannerTitle,
} from '@heidi/design-system/banner';
import { Button } from '@heidi/design-system/button';
import { useAtomValue } from 'jotai';
import { FormattedMessage } from 'react-intl';

interface EhrRePairBannerProps {
  /** EHR provider key — scopes the re-pair-required atom (must match the query's key). */
  provider: string;
  /** Human-readable brand name shown in the banner copy. */
  brand: string;
  /**
   * Optional "Reset & re-pair" action. Provided on the integrations connector
   * (where disconnecting reopens the pairing form); omitted mid-session, where
   * the banner is informational only.
   */
  onReset?: () => void;
}

/**
 * Persistent "Reconnect {brand}" banner shown when an EHR connection has lost its
 * pairing trust and needs to be reset and re-paired (ANZ-1193). Self-gates on the
 * per-provider re-pair-required atom, so it renders on whichever surface the user
 * is on — the integrations connector and the in-session appointment list alike.
 */
export const EhrRePairBanner = ({ provider, brand, onReset }: EhrRePairBannerProps) => {
  const isRePairRequired = useAtomValue(ehrRePairRequiredAtomFamily(provider));

  if (!isRePairRequired) return null;

  return (
    <Banner subVariant="error">
      <BannerContent>
        <BannerTitle>
          <FormattedMessage
            defaultMessage="Reconnect {brand}"
            id="9dwgXCM4JD"
            description="Banner title shown when a Best Practice connection must be reset and re-paired"
            values={{ brand }}
          />
        </BannerTitle>
        <BannerDescription>
          <FormattedMessage
            defaultMessage="Your {brand} connection has stopped working and needs to be reset and re-paired. Generate a new pairing code in {brand} and reconnect."
            id="qf4WJqWsHj"
            description="Banner body shown when a Best Practice connection must be reset and re-paired"
            values={{ brand }}
          />
        </BannerDescription>
      </BannerContent>
      {onReset && (
        <BannerAction>
          <Button variant="destructive" size="sm" onClick={onReset}>
            <FormattedMessage
              defaultMessage="Reset & re-pair"
              id="KE4E792mUq"
              description="Button that resets the Best Practice connection and opens the re-pair form"
            />
          </Button>
        </BannerAction>
      )}
    </Banner>
  );
};
